// Trees and grass that read as the real thing (2026-10-26; Devin: "the trees look like actual trees not green
// q-tips"). Built in code, no downloads: a leaf atlas painted at load (leafAtlas), trees grown from a few rules per kind
// (a trunk that forks into limbs and twigs, leaves as clusters of alpha-cut cards at the twig ends, the canopy's
// inner shade baked per vertex), and grass as patches of curved blades; their materials (wind, leaf and bark colors
// from palette.ts) at the bottom. board.ts places and instances them. The geometry is pure (typed arrays), so
// tests/look.test.ts holds the budgets.
import * as THREE from 'three';
import { hash3 } from './detail';
import { NOISE_GLSL, shared, weathered } from './look';
import { PALETTE } from './palette';

export const ATLAS = 512;
/** Atlas cells (each a quarter): a broadleaf cluster, birch leaves, a pine spray, a dense bush cluster. */
export const CELL = { oak: 0, birch: 1, pine: 2, bush: 3 } as const;

/** The leaf atlas, RGBA bytes: R the leaf's shade (veins, fold, light and dark leaves), G which of the kind's two
 *  palette colors it leans to, B 255 on a leaf and 0 on a twig (the shader paints twigs with bark), A coverage. */
export function leafAtlas(): Uint8Array {
  const N = ATLAS, H = N / 2, out = new Uint8Array(N * N * 4);
  // the gaps hold a mid leaf's color at no coverage, so a mip that averages a leaf with the gap beside it keeps the
  // leaf's shade and stays a leaf (black gaps darkened far trees and turned their leaves to bark: white specks on birches)
  for (let k = 0; k < N * N; k++) { out[k * 4] = 200; out[k * 4 + 1] = 128; out[k * 4 + 2] = 255; }
  /** Paints over what is there, at alpha a. */
  const put = (x: number, y: number, r: number, g: number, b: number, a: number) => {
    if (x < 0 || y < 0 || x >= N || y >= N || a <= 0) return;
    const k = (y * N + x) * 4, t = Math.min(1, a);
    out[k] = Math.round(out[k] * (1 - t) + r * t); out[k + 1] = Math.round(out[k + 1] * (1 - t) + g * t); out[k + 2] = Math.round(out[k + 2] * (1 - t) + b * t);
    out[k + 3] = Math.max(out[k + 3], Math.round(t * 255));
  };
  /** A leaf in cell (cx, cy) at (x, y) in cell units (0..1), pointing along angle a, len long and wid wide. */
  const leaf = (ox: number, oy: number, x: number, y: number, a: number, len: number, wid: number, shade: number, mix: number) => {
    const px = ox + x * H, py = oy + y * H, L = (len * H) / 2, W = (wid * H) / 2, ca = Math.cos(a), sa = Math.sin(a), R = Math.max(L, W) + 1;
    for (let j = Math.floor(py - R); j <= py + R; j++) for (let i = Math.floor(px - R); i <= px + R; i++) {
      if (i < ox || j < oy || i >= ox + H || j >= oy + H) continue; // stay in the cell, so mips don't bleed far
      const dx = i + 0.5 - px, dy = j + 0.5 - py, u = (dx * ca + dy * sa) / L, v = -dx * sa + dy * ca;
      if (u <= -1 || u >= 1) continue;
      const half = W * Math.pow(Math.sin(((u + 1) / 2) * Math.PI), 0.75), cov = Math.min(1, half - Math.abs(v) + 0.5);
      if (cov <= 0) continue;
      const across = Math.abs(v) / Math.max(half, 0.5), rib = Math.abs(v) < 0.7 ? 0.8 : 1;
      put(i, j, 255 * Math.min(1, shade * (1 - 0.28 * across) * rib * (0.9 + 0.12 * u)), 255 * mix, 255, cov);
    }
  };
  /** A twig from (x0, y0) to (x1, y1), w texels wide. */
  const twig = (ox: number, oy: number, x0: number, y0: number, x1: number, y1: number, w: number) => {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * H * 2);
    for (let s = 0; s <= n; s++) {
      const x = ox + (x0 + ((x1 - x0) * s) / n) * H, y = oy + (y0 + ((y1 - y0) * s) / n) * H;
      for (let dy = -w; dy <= w; dy++) for (let dx = -w; dx <= w; dx++) put(Math.round(x + dx), Math.round(y + dy), 120, 0, 0, 1 - Math.hypot(dx, dy) / (w + 1));
    }
  };
  const r = (k: number, s: number) => hash3(k, s, 77);
  /** A round cluster of n leaves on a few twigs from the middle. */
  const cluster = (cell: number, n: number, len: number, wid: number, spread: number) => {
    const ox = (cell % 2) * H, oy = (cell >> 1) * H;
    for (let t = 0; t < 5; t++) { const a = (t / 5) * 6.283 + r(cell, t); twig(ox, oy, 0.5, 0.5, 0.5 + Math.cos(a) * spread * 0.8, 0.5 + Math.sin(a) * spread * 0.8, 1); }
    for (let k = 0; k < n; k++) {
      const d = spread * Math.sqrt(r(cell * 999 + k, 1)), a = r(cell * 999 + k, 2) * 6.283, x = 0.5 + Math.cos(a) * d, y = 0.5 + Math.sin(a) * d;
      const dir = a + (r(cell * 999 + k, 3) - 0.5) * 1.4, l = len * (0.8 + 0.4 * r(cell * 999 + k, 4));
      // leaves deeper in the cluster sit under the outer ones and are darker
      leaf(ox, oy, x, y, dir, l, l * wid, 0.62 + 0.38 * (d / spread) * r(cell * 999 + k, 5) + 0.15, r(cell * 999 + k, 6));
    }
  };
  cluster(CELL.oak, 80, 0.16, 0.55, 0.38);
  cluster(CELL.birch, 110, 0.095, 0.72, 0.4);
  cluster(CELL.bush, 120, 0.12, 0.6, 0.4);
  // the pine: a spray, a twig up the middle with side twigs, needles off every twig
  {
    const ox = (CELL.pine % 2) * H, oy = (CELL.pine >> 1) * H;
    const needles = (x0: number, y0: number, x1: number, y1: number, len: number, k0: number) => {
      const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / 0.012), a = Math.atan2(y1 - y0, x1 - x0);
      for (let s = 0; s < n; s++) for (const side of [-1, 1]) {
        const t = s / n, x = x0 + (x1 - x0) * t, y = y0 + (y1 - y0) * t, na = a + side * (0.7 + 0.3 * r(k0 + s, side + 3)), l = len * (1 - 0.5 * t) * (0.8 + 0.4 * r(k0 + s, 9));
        leaf(ox, oy, x + Math.cos(na) * l * 0.5, y + Math.sin(na) * l * 0.5, na, l, 0.12, 0.7 + 0.35 * r(k0 + s, side + 5), r(k0 + s, 11));
      }
    };
    twig(ox, oy, 0.5, 0.95, 0.5, 0.06, 1);
    needles(0.5, 0.95, 0.5, 0.06, 0.2, 0);
    for (let b = 0; b < 6; b++) {
      const y = 0.85 - b * 0.13, side = b % 2 ? 1 : -1, x1 = 0.5 + side * (0.36 - b * 0.03), y1 = y - 0.14;
      twig(ox, oy, 0.5, y, x1, y1, 0); needles(0.5, y, x1, y1, 0.13, 100 * (b + 1));
    }
  }
  // clear each cell's border, so a card's edge (and the first mips) never pick up a neighbour's leaves
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) if (Math.min(x % H, y % H, H - 1 - (x % H), H - 1 - (y % H)) < 3) out[(y * N + x) * 4 + 3] = 0;
  return out;
}

/** A tree's or bush's geometry; uv into the atlas (leaves) or along the bark (wood). Attributes: aLeaf 1 on leaf cards;
 *  aShade the baked shade (deeper in the canopy and lower down is darker); aSway how much the wind moves a vertex (the
 *  trunk's foot none, the twig ends most); aPhase each card's own flutter. */
export type TreeKind = 'oak' | 'birch' | 'pine' | 'bush';

class Grower {
  pos: number[] = []; nrm: number[] = []; uv: number[] = []; leafA: number[] = []; shade: number[] = []; sway: number[] = []; phase: number[] = [];
  constructor(readonly seed: number) {}
  r(k: number) { return hash3(this.seed, k, 501); }
  private k = 0;
  next() { return this.r(this.k++); }
  /** A tapered limb from a to b, radii ra to rb, `sides` round; bark uv runs along it. */
  limb(a: THREE.Vector3, b: THREE.Vector3, ra: number, rb: number, sides: number, swayA: number, swayB: number) {
    const ax = new THREE.Vector3().subVectors(b, a), len = ax.length(); ax.normalize();
    const side = new THREE.Vector3(1, 0, 0); if (Math.abs(ax.x) > 0.9) side.set(0, 0, 1);
    const u = new THREE.Vector3().crossVectors(ax, side).normalize(), w = new THREE.Vector3().crossVectors(ax, u);
    const ring = (c: THREE.Vector3, r: number, k: number) => { const t = (k / sides) * Math.PI * 2; const n = u.clone().multiplyScalar(Math.cos(t)).addScaledVector(w, Math.sin(t)); return [c.clone().addScaledVector(n, r), n] as const; };
    for (let k = 0; k < sides; k++) {
      const [p0, n0] = ring(a, ra, k), [p1, n1] = ring(a, ra, k + 1), [q0, m0] = ring(b, rb, k), [q1, m1] = ring(b, rb, k + 1);
      const quad: [THREE.Vector3, THREE.Vector3, number, number, number][] = [[p0, n0, k / sides, 0, swayA], [q0, m0, k / sides, len, swayB], [p1, n1, (k + 1) / sides, 0, swayA], [p1, n1, (k + 1) / sides, 0, swayA], [q0, m0, k / sides, len, swayB], [q1, m1, (k + 1) / sides, len, swayB]];
      for (const [p, n, s, t, sw] of quad) this.vert(p, n, s, t * 2, 0, 0.85, sw, 0);
    }
  }
  /** A leaf card centered at c, size s, facing f (spun by spin about it), mapped to an atlas cell; its normals lean
   *  out from the crown's middle `mid` so the canopy shades round; shade by depth and height. */
  card(c: THREE.Vector3, f: THREE.Vector3, spin: number, s: number, cell: number, mid: THREE.Vector3, crownR: number, sw: number, stretch = 1) {
    const side = Math.abs(f.y) > 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const u = new THREE.Vector3().crossVectors(side, f).normalize(), v = new THREE.Vector3().crossVectors(f, u);
    const cu = u.clone().multiplyScalar(Math.cos(spin)).addScaledVector(v, Math.sin(spin)), cv = v.clone().multiplyScalar(Math.cos(spin)).addScaledVector(u, -Math.sin(spin));
    const u0 = (cell % 2) * 0.5, v0 = (cell >> 1) * 0.5, ph = this.next() * 6.283;
    const corner = (a: number, b: number) => c.clone().addScaledVector(cu, (a * s) / 2).addScaledVector(cv, (b * s * stretch) / 2);
    const pts = [[-1, -1], [1, -1], [1, 1], [-1, -1], [1, 1], [-1, 1]];
    for (const [a, b] of pts) {
      const p = corner(a, b), n = p.clone().sub(mid).normalize().multiplyScalar(0.75).addScaledVector(f, 0.25).add(new THREE.Vector3(0, 0.25, 0)).normalize();
      const depth = Math.min(1, p.clone().sub(mid).length() / crownR), height = Math.min(1, Math.max(0, (p.y - (mid.y - crownR)) / (2 * crownR)));
      // texel half a step in from the cell's edge, so mips never reach the next cell
      this.vert(p, n, u0 + 0.5 * (0.004 + 0.992 * (a + 1) / 2), v0 + 0.5 * (0.004 + 0.992 * (b + 1) / 2), 1, (0.7 + 0.3 * depth) * (0.82 + 0.18 * height), sw, ph);
    }
  }
  vert(p: THREE.Vector3, n: THREE.Vector3, s: number, t: number, leaf: number, shade: number, sw: number, ph: number) {
    this.pos.push(p.x, p.y, p.z); this.nrm.push(n.x, n.y, n.z); this.uv.push(s, t); this.leafA.push(leaf); this.shade.push(shade); this.sway.push(sw); this.phase.push(ph);
  }
  geometry() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute('aLeaf', new THREE.Float32BufferAttribute(this.leafA, 1));
    g.setAttribute('aShade', new THREE.Float32BufferAttribute(this.shade, 1));
    g.setAttribute('aSway', new THREE.Float32BufferAttribute(this.sway, 1));
    g.setAttribute('aPhase', new THREE.Float32BufferAttribute(this.phase, 1));
    return g;
  }
}

const V = (x = 0, y = 0, z = 0) => new THREE.Vector3(x, y, z);
/** A direction at azimuth az, tilted `tilt` from straight up. */
const dirAt = (az: number, tilt: number) => V(Math.cos(az) * Math.sin(tilt), Math.cos(tilt), Math.sin(az) * Math.sin(tilt));

/** Grows one tree of a kind (variant picks its seed). Sized so its crown is about 1 m round (board.ts scales each by
 *  its crown radius) and an oak stands about 3 m. */
export function growTree(kind: TreeKind, variant: number, lite = false): THREE.BufferGeometry {
  // lite (the Light tier: it must stay as fast as main): the same tree with no root flare or twigs, rounder limbs
  // fewer-sided, and half the leaf cards, each a little bigger
  const g = new Grower(variant * 13 + kind.length * 101), sides = (n: number) => (lite ? Math.max(3, n - 2) : n);
  const leaves: { c: THREE.Vector3; f: THREE.Vector3 }[] = [];
  if (kind === 'oak' || kind === 'birch') {
    const oak = kind === 'oak', H = oak ? 1.6 + g.next() * 0.3 : 2.0 + g.next() * 0.4;
    const lean = V((g.next() - 0.5) * 0.25, 0, (g.next() - 0.5) * 0.25), top = V(lean.x, H, lean.z);
    if (oak && !lite) g.limb(V(), V(lean.x * 0.1, 0.25, lean.z * 0.1), 0.27, 0.17, 6, 0, 0); // the root flare
    g.limb(oak && !lite ? V(lean.x * 0.1, 0.25, lean.z * 0.1) : V(), top, oak ? 0.17 : 0.1, oak ? 0.12 : 0.075, sides(oak ? 6 : 5), 0, 0.15);
    // the limbs fork from the top of the trunk, and twigs from the limbs; a leaf cluster at every end
    const limbs = oak ? 4 : 3, az0 = g.next() * 6.283;
    for (let l = 0; l < limbs; l++) {
      const az = az0 + (l / limbs) * 6.283 + (g.next() - 0.5) * 0.8, tilt = oak ? 0.55 + g.next() * 0.4 : 0.25 + g.next() * 0.3;
      const len = oak ? 0.85 + g.next() * 0.35 : 1.0 + g.next() * 0.5, from = top.clone().addScaledVector(V(0, -1, 0), oak ? 0.15 + g.next() * 0.45 : 0.3 + g.next() * 0.6);
      const end = from.clone().addScaledVector(dirAt(az, tilt), len);
      g.limb(from, end, oak ? 0.1 : 0.05, oak ? 0.05 : 0.025, sides(oak ? 5 : 4), 0.15, 0.5);
      leaves.push({ c: end, f: dirAt(az, tilt * 0.6) });
      for (let t = 0; t < (oak ? 2 : 3); t++) {
        const at = from.clone().lerp(end, 0.5 + 0.4 * g.next()), taz = az + (g.next() - 0.5) * 2.2, ttilt = oak ? 0.7 + g.next() * 0.6 : 0.6 + g.next() * 0.6;
        const tend = at.clone().addScaledVector(dirAt(taz, ttilt), oak ? 0.45 + g.next() * 0.3 : 0.35 + g.next() * 0.25);
        if (!lite) g.limb(at, tend, oak ? 0.045 : 0.025, 0.018, 3, 0.45, 0.8);
        leaves.push({ c: tend, f: dirAt(taz, ttilt * 0.5) });
      }
    }
  } else if (kind === 'pine') {
    const H = 3.1 + g.next() * 0.5;
    g.limb(V(), V(0, H, 0), 0.13, 0.03, sides(5), 0, 0.4);
    // whorls of branches, shorter toward the top, each a pair of needle sprays (one flat, one tipped) along it
    const mid = V(0, H * 0.45, 0);
    for (let w = 0; w < 6; w++) {
      const y = 0.55 + w * ((H - 0.9) / 6), reach = 1.05 * (1 - w / 6.5) + 0.2, n = 5, az0 = g.next() * 6.283;
      for (let b = 0; b < n; b++) {
        const az = az0 + (b / n) * 6.283 + (g.next() - 0.5) * 0.5, d = dirAt(az, Math.PI / 2 + 0.25), c = V(0, y, 0).addScaledVector(d, reach / 2);
        const along = d.clone(), up = V(0, 1, 0), f = up.clone().addScaledVector(along, -up.dot(along)).normalize();
        for (const tip of lite ? [0.45] : [0, 0.9]) {
          const face = f.clone().applyAxisAngle(along, tip);
          g.card(c, face, cardSpin(face, along) + Math.PI, reach * 0.62, CELL.pine, mid, H * 0.5, 0.3 + 0.7 * (y / H), reach / (reach * 0.62));
        }
      }
    }
    g.card(V(0, H - 0.25, 0), V(1, 0, 0), 0, 0.7, CELL.pine, mid, H * 0.5, 0.9); g.card(V(0, H - 0.25, 0), V(0, 0, 1), 0, 0.7, CELL.pine, mid, H * 0.5, 0.9);
    return normalizeCrown(g.geometry(), 1.25);
  } else {
    // a bush: a few short stems and leaf clusters close to the ground
    for (let s = 0; s < 3; s++) { const az = s * 2.1 + g.next(); if (!lite) g.limb(V(), dirAt(az, 0.6).multiplyScalar(0.35), 0.04, 0.02, 3, 0, 0.3); }
    for (let k = 0; k < 7; k++) { const az = k * 0.9 + g.next(), c = V(Math.cos(az) * 0.35 * g.next(), 0.3 + g.next() * 0.3, Math.sin(az) * 0.35 * g.next()); leaves.push({ c, f: c.clone().sub(V(0, 0.1, 0)).normalize() }); }
  }
  // the leaf clusters: three or four crossed cards at each end, and a few inside to close the crown
  const mid = leaves.reduce((m, l) => m.add(l.c), V()).multiplyScalar(1 / leaves.length);
  const crownR = Math.max(...leaves.map((l) => l.c.distanceTo(mid))) + 0.4;
  const cell = CELL[kind === 'oak' ? 'oak' : kind === 'birch' ? 'birch' : 'bush'], size = (kind === 'oak' ? 1.05 : kind === 'birch' ? 0.85 : 0.75) * (lite ? 1.3 : 1);
  for (const l of leaves) for (let k = 0; k < (lite ? 2 : kind === 'oak' ? 5 : 4); k++) {
    const f = l.f.clone().add(V(g.next() - 0.5, g.next() * 0.6, g.next() - 0.5).multiplyScalar(1.2)).normalize();
    g.card(l.c.clone().addScaledVector(V(g.next() - 0.5, g.next() - 0.5, g.next() - 0.5), 0.35), f, g.next() * 6.283, size * (0.8 + 0.4 * g.next()), cell, mid, crownR, kind === 'bush' ? 0.3 : 1);
  }
  for (let k = 0; k < (kind === 'bush' ? 0 : lite ? 2 : 6); k++) {
    const c = mid.clone().add(V(g.next() - 0.5, g.next() - 0.5, g.next() - 0.5).multiplyScalar(crownR * 0.8));
    g.card(c, c.clone().sub(mid).normalize(), g.next() * 6.283, size * 1.1, cell, mid, crownR, 0.8);
  }
  return normalizeCrown(g.geometry(), kind === 'bush' ? 0.8 : 1.4);
}

/** The spin that lays a card's long side along a branch. */
function cardSpin(face: THREE.Vector3, along: THREE.Vector3) {
  const side = Math.abs(face.y) > 0.9 ? V(1, 0, 0) : V(0, 1, 0), u = new THREE.Vector3().crossVectors(side, face).normalize(), v = new THREE.Vector3().crossVectors(face, u);
  return Math.atan2(along.dot(u), along.dot(v)) * -1;
}

/** Scales a tree about its foot so its widest horizontal reach is `r`. */
function normalizeCrown(g: THREE.BufferGeometry, r: number) {
  const p = g.attributes.position;
  let reach = 0;
  for (let k = 0; k < p.count; k++) reach = Math.max(reach, Math.hypot(p.getX(k), p.getZ(k)));
  g.scale(r / reach, r / reach, r / reach);
  return g;
}

/** A patch of grass: `n` curved blades over a 0.8 m round, each two segments and a tip (three triangles), leaning out
 *  and bending over. aH is the height along a blade (0 root, 1 tip: color and wind), aVar each blade's own shade and
 *  dryness. Normals point up, as the felt's, so the blades sit in the ground's light. */
/** A grass patch's radius before its instance scale (where blades root; they lean and bend out past it). */
export const PATCH_R = 0.4;
/** The farthest the breeze moves a blade's tip, along +x and half that along +z (grassMaterial; before the instance's
 *  scale): the gust's 0.08 and the flutter's 0.012 at full height. It never pushes past -0.012 the other way. */
export const GRASS_WIND = { max: 0.092, min: -0.012 };
/** How far a patch reaches from its middle at the most, the breeze included, before its instance scale. */
export function grassReach() {
  const p = grassPatch().attributes.position;
  let r = 0;
  for (let i = 0; i < p.count; i++) r = Math.max(r, Math.hypot(p.getX(i), p.getZ(i)));
  return r + Math.hypot(GRASS_WIND.max, GRASS_WIND.max / 2);
}
export function grassPatch(n = 16, seed = 1) {
  const pos: number[] = [], nrm: number[] = [], h: number[] = [], vr: number[] = [], idx: number[] = [];
  for (let b = 0; b < n; b++) {
    const r = PATCH_R * Math.sqrt(hash3(b, seed, 1)), a = hash3(b, seed, 2) * 6.283, x = Math.cos(a) * r, z = Math.sin(a) * r;
    const face = hash3(b, seed, 3) * 6.283, bend = hash3(b, seed, 4) * 6.283, tall = 0.16 + 0.18 * hash3(b, seed, 5), w = 0.022 + 0.012 * hash3(b, seed, 6);
    const fx = Math.cos(face), fz = Math.sin(face), bx = Math.cos(bend) * tall * 0.45, bz = Math.sin(bend) * tall * 0.45, v = hash3(b, seed, 7);
    // the blade's spine at t: up, curving over toward its bend
    const at = (t: number, s: number) => [x + bx * t * t + fx * w * s * (1 - t), tall * t * (1 - 0.25 * t * t), z + bz * t * t + fz * w * s * (1 - t)];
    // five vertices shared by three triangles (indexed: the phone tier runs this too, Astra F1)
    const o = pos.length / 3;
    for (const [t, side] of [[0, -1], [0, 1], [0.55, -1], [0.55, 1], [1, 0]]) { pos.push(...at(t, side)); nrm.push(0, 1, 0); h.push(t); vr.push(v); }
    idx.push(o, o + 1, o + 3, o, o + 3, o + 2, o + 2, o + 3, o + 4);
  }
  const g = new THREE.BufferGeometry();
  g.setIndex(idx);
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('aH', new THREE.Float32BufferAttribute(h, 1));
  g.setAttribute('aVar', new THREE.Float32BufferAttribute(vr, 1));
  return g;
}

/** The atlas's mip chain (level 0 is the atlas), each level a box average of the one above with its coverage kept:
 *  averaging leaves with the gaps around them pulls alpha under the cut, so a far tree would go sparse, and any fixed
 *  boost fills it in solid instead (Astra F3). Each cell's alpha is scaled so the share of texels over the 0.5 cut
 *  matches level 0's while a cell is 16 texels or more; below that (a card a few pixels on screen) the cell is one soft
 *  round blob of the same area, which still survives the filtering (Astra's re-check: the box-averaged tail erased
 *  far trees). */
export function leafMips(base: Uint8Array) {
  const levels = [{ data: base, width: ATLAS, height: ATLAS }];
  /** A cell's alpha histogram (256 bins) at a level n texels wide. */
  const hist = (d: Uint8Array, n: number, cell: number) => {
    const h = n / 2, ox = (cell % 2) * h, oy = (cell >> 1) * h, out = new Uint32Array(256);
    for (let y = 0; y < h; y++) for (let x = 0; x < h; x++) out[d[((oy + y) * n + ox + x) * 4 + 3]]++;
    return out;
  };
  const want = [0, 1, 2, 3].map((c) => hist(base, ATLAS, c).slice(128).reduce((p, q) => p + q, 0) / ((ATLAS / 2) ** 2));
  for (let n = ATLAS / 2; n >= 1; n /= 2) {
    const up = levels[levels.length - 1].data, m = n * 2, d = new Uint8Array(n * n * 4);
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      const k = (2 * y * m + 2 * x) * 4, r = k + m * 4;
      for (let c = 0; c < 4; c++) d[(y * n + x) * 4 + c] = (up[k + c] + up[k + 4 + c] + up[r + c] + up[r + 4 + c] + 2) >> 2;
    }
    if (n / 2 < 16) for (let cell = 0; cell < 4; cell++) {
      const h = n / 2, ox = (cell % 2) * h, oy = (cell >> 1) * h, R = Math.sqrt(want[cell] / Math.PI) * h;
      for (let y = 0; y < h; y++) for (let x = 0; x < h; x++) {
        const dist = Math.hypot(x + 0.5 - h / 2, y + 0.5 - h / 2);
        // two texels or fewer a cell: a card under a pixel or two, drawn whole
        d[((oy + y) * n + ox + x) * 4 + 3] = h <= 2 ? 255 : Math.round(255 * Math.min(1, Math.max(0, R - dist + 0.5)));
      }
    }
    else for (let cell = 0; cell < 4; cell++) {
      // the alpha that the wanted share of texels reach, scaled up or down to sit on the cut
      const hs = hist(d, n, cell), h = n / 2, need = want[cell] * h * h;
      let v = 255, seen = hs[255];
      while (v > 0 && seen < need) seen += hs[--v];
      const scale = 128 / (v + 0.5), ox = (cell % 2) * h, oy = (cell >> 1) * h;
      for (let y = 0; y < h; y++) for (let x = 0; x < h; x++) { const i = ((oy + y) * n + ox + x) * 4 + 3; d[i] = Math.min(255, Math.round(d[i] * scale)); }
    }
    levels.push({ data: d, width: n, height: n });
  }
  return levels;
}

/** The leaf atlas as a texture with its coverage-keeping mips, made once. */
let atlasTex: THREE.DataTexture | null = null;
export function leafTexture() {
  if (atlasTex) return atlasTex;
  const mips = leafMips(leafAtlas()), t = new THREE.DataTexture(mips[0].data, ATLAS, ATLAS, THREE.RGBAFormat);
  t.mipmaps = mips as unknown as THREE.DataTexture['mipmaps'];
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = false; t.anisotropy = 4;
  t.needsUpdate = true;
  return (atlasTex = t);
}

/** The trees' seasonal uniforms, one set a kind (sky.ts paintSeason sets them): uBare, the share of leaf cards gone
 *  (winter thins the oaks and birches to their limbs); uFrost, how far the cards left are snow, not leaves (a bare
 *  winter tree's cards become the snow lying along its limbs: from the match camera bare twigs alone read as black
 *  scratches); uAccent, the share of trees in the kind's accent color (PALETTE.accent: spring blossom, autumn's deep
 *  reds), a tree's own draw (aTree) deciding, the same on every tier. */
type TreeSeason = { uBare: { value: number }; uFrost: { value: number }; uAccent: { value: number } };
const treeSeason = (): TreeSeason => ({ uBare: { value: 0 }, uFrost: { value: 0 }, uAccent: { value: 0 } });
export const TREE_SEASON: Record<TreeKind, TreeSeason> = { oak: treeSeason(), birch: treeSeason(), pine: treeSeason(), bush: treeSeason() };
/** A bare tree's leaf cards collapse to its foot in the vertex shader (no fragments, cheaper than a discard), the
 *  same in the shadow pass; which cards go is each card's own flutter phase. */
const BARE = `
  if (aLeaf > 0.5 && fract(aPhase * 1.618) < uBare) transformed = vec3(0.0);`;

/** The wind, the same in the lit pass and the shadow pass so a tree's shadow moves with it: the whole tree leans
 *  (more toward the twig ends, aSway), out of step tree to tree, and each leaf card flutters on its own (aPhase). */
const WIND = `
  {
    vec3 lkRoot = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    float ph = lkRoot.x * 0.37 + lkRoot.z * 0.23;
    float s = (sin(uTime * 1.1 + ph) * 0.7 + sin(uTime * 2.3 + ph * 1.7) * 0.3) * 0.07 * aSway;
    transformed.x += s; transformed.z += s * 0.6;
    transformed += normal * sin(uTime * 5.0 + aPhase + ph) * 0.03 * aLeaf;
  }`;

/** A tree kind's material: leaves from the atlas in the kind's two palette colors (palette.ts, shared by reference, so
 *  a season repaints them), bark below them in the kind's bark color, cut in the shader (birch white with dark marks,
 *  the others furrowed). Leaf cards are two-sided with their normals kept leaning out of the crown on both faces, so a
 *  canopy shades round, not card by card. customDepthMaterial casts the leaves' shadow, not the cards'. */
export function treeMaterial(kind: TreeKind, leafA: THREE.Color, leafB: THREE.Color, bark: THREE.Color) {
  const u = { uTime: shared.uTime, uLeaves: { value: leafTexture() }, uLeafA: { value: leafA }, uLeafB: { value: leafB }, uBark: { value: bark }, uSunDir: { value: PALETTE.sun.dir },
    uAccentCol: { value: PALETTE.accent[kind] }, ...TREE_SEASON[kind] };
  const ATTR = 'attribute float aLeaf; attribute float aShade; attribute float aSway; attribute float aPhase; attribute float aTree; uniform float uTime; uniform float uBare; uniform float uAccent;';
  const mat = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
  mat.userData.uLeaves = u.uLeaves.value; // its shader's texture, kept where src/site/free3d.ts freeScene finds it
  if (kind === 'birch') mat.defines = { BIRCH: '' };
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>\n${ATTR}\nvarying float vLeaf; varying float vShade; varying vec2 vLUv; varying float vAccent;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n  vLeaf = aLeaf; vShade = aShade; vLUv = uv; vAccent = step(aTree, uAccent - 0.001);${WIND}${BARE}`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
varying float vLeaf; varying float vShade; varying vec2 vLUv; varying float vAccent;
uniform sampler2D uLeaves; uniform vec3 uLeafA, uLeafB, uBark, uSunDir, uAccentCol; uniform float uFrost;` + NOISE_GLSL)
      .replace('#include <color_fragment>', `
  {
    vec4 t = texture2D(uLeaves, vLUv);
    vec3 col;
    if (vLeaf > 0.5) {
      if (t.a < 0.5) discard; // the mips keep each cell's coverage (leafMips)
      // each leaf its own mix of the two colors, lit by its shade in the atlas; a twig in the cluster takes the bark
      // an accent tree (blossom, autumn red) wears its accent over most of its leaves
      vec3 leaf = mix(mix(uLeafA, uLeafB, t.g), uAccentCol * (0.88 + 0.24 * t.g), vAccent * 0.88);
      leaf = mix(leaf, uSnowCol * (0.82 + 0.2 * t.g), uFrost); // winter: snow along the limbs
      col = mix(uBark * 0.6, leaf * (0.55 + 0.6 * t.r), smoothstep(0.3, 0.7, t.b));
    } else {
      // bark: uv.x round the limb, uv.y along it
      vec2 b = vLUv * vec2(1.0, 1.0);
#ifdef BIRCH
      float mark = smoothstep(0.66, 0.74, lkNoise(vec2(b.x * 5.0, b.y * 14.0))) + smoothstep(0.8, 0.86, lkNoise(vec2(b.x * 3.0, b.y * 3.0 + 9.0)));
      col = uBark * (0.92 + 0.1 * lkNoise(b * vec2(9.0, 30.0))) * (1.0 - 0.75 * clamp(mark, 0.0, 1.0));
#else
      float furrow = abs(fract(b.x * 7.0 + lkNoise(b * vec2(3.0, 2.0)) * 0.8) - 0.5) * 2.0;
      col = uBark * (0.85 + 0.25 * lkNoise(b * vec2(14.0, 6.0))) * mix(0.55, 1.08, smoothstep(0.15, 0.6, furrow));
#endif
    }
    diffuseColor.rgb = col * vShade;
  }
  #include <color_fragment>`)
      .replace('#include <normal_fragment_begin>', '#include <normal_fragment_begin>\n  if (vLeaf > 0.5) normal = normalize(vNormal); // both faces lean out of the crown')
      .replace('#include <opaque_fragment>', `{
    // sun through the leaves: a leaf seen with the sun behind it glows a little
    float through = pow(clamp(dot(normalize(-vViewPosition), normalize((viewMatrix * vec4(uSunDir, 0.0)).xyz)), 0.0, 1.0), 4.0);
    outgoingLight += diffuseColor.rgb * uLeafB * through * 0.6 * vLeaf;
  }
  #include <opaque_fragment>`);
    weathered(sh); // snow on the canopy's top and along the limbs, wet bark
  };
  mat.customProgramCacheKey = () => `tree-${kind}`;
  return mat;
}

/** The shadow pass's material for a kind of tree: the same wind and the same bare cards, and a leaf card cut to its
 *  leaves. One a kind (each kind sheds its own share). */
const depthMats = new Map<TreeKind, THREE.MeshDepthMaterial>();
export function treeDepthMaterial(kind: TreeKind) {
  const had = depthMats.get(kind);
  if (had) return had;
  const m = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, side: THREE.DoubleSide });
  m.userData.uLeaves = leafTexture();
  m.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, { uTime: shared.uTime, uLeaves: { value: leafTexture() }, uBare: TREE_SEASON[kind].uBare });
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aLeaf; attribute float aSway; attribute float aPhase; uniform float uTime; uniform float uBare; varying float vLeaf; varying vec2 vLUv;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n  vLeaf = aLeaf; vLUv = uv;${WIND}${BARE}`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying float vLeaf; varying vec2 vLUv; uniform sampler2D uLeaves;`)
      .replace('#include <clipping_planes_fragment>', '#include <clipping_planes_fragment>\n  if (vLeaf > 0.5 && texture2D(uLeaves, vLUv).a < 0.5) discard;');
  };
  m.customProgramCacheKey = () => 'tree-depth';
  depthMats.set(kind, m);
  return m;
}

/** Grass blades: root to tip in the palette's grass colors, some blades gone straw (aVar), a breeze bending the tips,
 *  shrinking away past `far` meters from the camera (a blade there is under a pixel; the felt carries it). Normals up
 *  on both faces, so a blade is lit as the ground it grows from. */
export function grassMaterial(root: THREE.Color, tip: THREE.Color, dry: THREE.Color, far = 150) {
  const u = { uTime: shared.uTime, uRoot: { value: root }, uTip: { value: tip }, uDry: { value: dry }, uCover: shared.uCover, uSnowCol: { value: PALETTE.snow } };
  const mat = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aH; attribute float aVar; uniform float uTime; uniform float uCover; varying float vH; varying float vVar;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
  vH = aH; vVar = aVar;
  {
    vec3 lkRoot = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    transformed *= 1.0 - smoothstep(${(far * 0.6).toFixed(1)}, ${far.toFixed(1)}, distance(cameraPosition, lkRoot));
    float gust = sin(uTime * 1.4 + lkRoot.x * 0.21 + lkRoot.z * 0.13) * 0.5 + 0.5;
    float w = (0.02 + 0.06 * gust) * aH * aH + sin(uTime * 3.1 + aVar * 40.0) * 0.012 * aH; // keep GRASS_WIND in step
    transformed.x += w; transformed.z += w * 0.5;
    transformed.y *= 1.0 - 0.7 * uCover; // under snow only the tips show
  }`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vH; varying float vVar; uniform vec3 uRoot, uTip, uDry, uSnowCol; uniform float uCover;')
      .replace('#include <color_fragment>', `
  diffuseColor.rgb = mix(uRoot, mix(uTip, uDry, step(0.86, vVar) * 0.8), vH) * (0.86 + 0.28 * vVar) * (0.62 + 0.38 * vH);
  diffuseColor.rgb = mix(diffuseColor.rgb, uSnowCol, uCover * (0.55 - 0.35 * vH)); // the roots in the snow, frost on the tips
  #include <color_fragment>`)
      .replace('#include <normal_fragment_begin>', '#include <normal_fragment_begin>\n  normal = normalize(vNormal);');
  };
  mat.customProgramCacheKey = () => 'grass';
  return mat;
}
